import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, afterNextRender, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { RuntimeConfigService } from '../services/common/runtime-config.service';
import {
  SIMULATE_ASSET_STORAGE_KEY,
  SimulateAssetMode,
  createSimulateAssetScenario,
} from '../utils/simulate-asset-failure.util';
import { markSimulationEnabled } from '../utils/simulate-failure.state';
import {
  SIMULATE_FAILURE_SCOPES,
  SIMULATE_FAILURE_STORAGE_KEY,
  buildSimulatedErrorResponse,
  createSimulateFailureScenario,
  matchesSimulateFailure,
  parseSimulateFailureScenario,
} from '../utils/simulate-failure.util';

/**
 * QA switch that answers a chosen endpoint with a synthetic HTTP 5xx, so the
 * `SRV` toast can be smoke-tested without a code change or a backend fault.
 * Tester instructions live in `utils/simulate-failure.util.ts`.
 *
 * ## Why it cannot affect production
 *
 * 1. The production build swaps `simulate-failure.registration.ts` for an empty
 *    list (`angular.json` fileReplacements), so this file is not in the bundle.
 *    (A conditional on `environment.production` was tried first and measured to
 *    leave the code in the bundle.)
 * 2. The check below repeats that guard, so it also holds if registration moves.
 * 3. `RuntimeConfig.simulateFailureEnabled` must be `true`, and
 *    `RuntimeConfigService.validate()` aborts boot if it is `true` in a
 *    production build.
 *
 * ## Position in the chain
 *
 * Registered directly AFTER `httpErrorInterceptor`: that one must see the synthetic
 * error on its way back up, exactly as it would see a real one.
 */
export const simulateFailureInterceptor: HttpInterceptorFn = (request, next) => {
  if (environment.production) return next(request);
  const config = inject(RuntimeConfigService);
  if (!config.simulateFailureEnabled()) return next(request);

  // Tells the schema-asset seam (no injection context) that the switch is on.
  markSimulationEnabled();
  installSimulateFailureConsoleHelper();

  const scenario = readScenario();
  if (!scenario || !matchesSimulateFailure(scenario, request)) return next(request);

  console.warn(`[simulate-failure] ${request.method} ${request.url} -> synthetic ${scenario.status} (scope ${scenario.scope})`);
  return deliverAfterNextRender(inject(Injector), () => buildSimulatedErrorResponse(request, scenario));
};

/**
 * Fail the request once Angular has finished rendering, never synchronously.
 *
 * A page that starts its load in `ngOnInit` (User Management, Profile, ...)
 * would otherwise receive the error before its `<interactive-toast>` child has
 * been created and subscribed. PrimeNG drops a message added before its toast
 * host exists, so the page's handler ran and logged its `[page-load]` record but
 * no toast appeared. A real HTTP failure is always asynchronous, so it never
 * hits this. Waiting for the next completed render is the event that actually
 * matters ("the page and its toast host are on screen"), not a guessed delay.
 *
 * Unsubscribing before the render cancels the pending delivery.
 */
function deliverAfterNextRender(injector: Injector, buildError: () => HttpErrorResponse): Observable<never> {
  return new Observable<never>((subscriber) => {
    const renderHook = afterNextRender(() => subscriber.error(buildError()), { injector });
    return () => renderHook.destroy();
  });
}

function readScenario() {
  try {
    return parseSimulateFailureScenario(localStorage.getItem(SIMULATE_FAILURE_STORAGE_KEY), Date.now());
  } catch {
    // Storage can be blocked (private window, site data disabled): behave as "no scenario".
    return null;
  }
}

let helperInstalled = false;

/** Exposes `__simulateSrv`, `__simulateSrvScopes` and `__simulateSrvClear` on `window`, once. */
function installSimulateFailureConsoleHelper(): void {
  if (helperInstalled) return;
  helperInstalled = true;
  const target = window as unknown as Record<string, unknown>;

  target['__simulateSrv'] = (scope: string, status: number = 500): string => {
    const scenario = createSimulateFailureScenario(scope, status, Date.now());
    localStorage.setItem(SIMULATE_FAILURE_STORAGE_KEY, JSON.stringify(scenario));
    return `Simulating ${scenario.status} on ${scenario.scope} for 1 hour. Reload or navigate to the page. __simulateSrvClear() to stop.`;
  };
  target['__simulateSrvScopes'] = (): string[] => Object.keys(SIMULATE_FAILURE_SCOPES);
  target['__simulateSrvClear'] = (): string => {
    localStorage.removeItem(SIMULATE_FAILURE_STORAGE_KEY);
    return 'Simulation cleared.';
  };

  // Companion switch for the JSON assets a filing page loads — the sheet form
  // definitions and the support files (types.dict, msic_options, scenarios.config).
  // Modes: 'missing' (host serves index.html → ASSET), a 4xx/5xx (ASSET),
  // 'invalid-json' (PARSE), 'offline' (NET).
  target['__simulateAsset'] = (role: string | number, mode: SimulateAssetMode = 404): string => {
    const scenario = createSimulateAssetScenario(role, mode, Date.now());
    localStorage.setItem(SIMULATE_ASSET_STORAGE_KEY, JSON.stringify(scenario));
    return `Simulating "${scenario.mode}" on ${scenario.role} for 1 hour. Open or switch to that sheet (tick "Disable cache"). __simulateAssetClear() to stop.`;
  };
  target['__simulateAssetClear'] = (): string => {
    localStorage.removeItem(SIMULATE_ASSET_STORAGE_KEY);
    return 'Asset simulation cleared.';
  };
}
